/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ISlider } from './types';

export const SmSlider = forwardRef<HTMLInputElement, ISlider>(
  (
    {
      size = 'medium',
      min,
      max,
      disabled,
      onChange,
      defaultValue,
      step = 100,
      ...props
    },
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    ref,
  ) => {
    const [maxValue, setMaxValue] = useState(() => {
      return Math.min(max, (defaultValue ?? max) as number);
    });

    const sliderClassName = classNames('c-slider', `-${size}`, {
      '-disabled': disabled,
    });

    const maxValueRef = useRef<HTMLInputElement>(null);
    const range = useRef<HTMLDivElement>(null);

    const getPercent = useCallback(
      (value: number) => Math.round(((value - min) / (max - min)) * 100),
      [min, max],
    );

    useEffect(() => {
      if (maxValueRef.current) {
        const minPercent = getPercent(min);
        const maxPercent = getPercent(+maxValueRef.current.value);

        if (range.current) {
          range.current.style.left = `${minPercent}%`;
          range.current.style.width = `${maxPercent - minPercent}%`;
        }
      }
    }, [getPercent, min]);

    useEffect(() => {
      if (range.current && maxValue) {
        const maxPercent = getPercent(maxValue);

        range.current.style.left = `0%`;
        range.current.style.width = `${maxPercent}%`;
      }
    }, [maxValue, getPercent]);

    useEffect(() => {
      setMaxValue(Math.min(max, (defaultValue ?? max) as number));
    }, [defaultValue, max]);

    return (
      <div
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        className={sliderClassName}
        style={{ position: 'relative' }}
      >
        <input
          className="c-slider__thumb -right"
          type="range"
          min={min}
          max={max}
          value={maxValue}
          disabled={disabled}
          ref={maxValueRef}
          step={step}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = Math.max(Number(event.target.value), min);
            setMaxValue(value);
            onChange?.({ curMin: min, curMax: value });
          }}
          style={{
            width: '100%',
          }}
          data-testid="testSliderInput"
        />

        <div className="c-slider__bar" style={{ width: '100%' }}>
          <div className="c-slider__track" />
          <div ref={range} className="c-slider__range" />
        </div>
      </div>
    );
  },
);
